import { useEffect, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { ptBR } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** Converte aaaa-mm-dd -> dd/mm/aaaa */
const isoParaBR = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

/** Converte dd/mm/aaaa -> aaaa-mm-dd (vazio se incompleto/inválido) */
const brParaISO = (br: string) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br);
  if (!m) return "";
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  const ano = Number(m[3]);
  if (dia < 1 || dia > 31 || mes < 1 || mes > 12 || ano < 1900) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const mascara = (texto: string) => {
  const d = texto.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

type Props = {
  id?: string;
  /** Valor no formato aaaa-mm-dd */
  value: string;
  /** Recebe o novo valor em aaaa-mm-dd */
  onChange: (iso: string) => void;
  className?: string;
  inputClassName?: string;
  /** Botões renderizados entre o input e o botão do picker */
  acoes?: React.ReactNode;
};

/** Campo de data no padrão brasileiro (dd/mm/aaaa) */
export function DataBR({ id, value, onChange, className, inputClassName, acoes }: Props) {
  const [texto, setTexto] = useState(() => isoParaBR(value));

  useEffect(() => {
    const atual = brParaISO(texto);
    if (value !== atual) setTexto(isoParaBR(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [aberto, setAberto] = useState(false);
  const selecionada = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Input
        id={id}
        className={cn(inputClassName ?? "flex-1")}
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        value={texto}
        onChange={(e) => {
          const t = mascara(e.target.value);
          setTexto(t);
          const iso = brParaISO(t);
          if (iso) onChange(iso);
        }}
      />
      {acoes}
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Abrir calendário"
            className="shrink-0"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={ptBR}
            captionLayout="dropdown"
            {...(selecionada ? { defaultMonth: selecionada, selected: selecionada } : {})}
            onSelect={(d) => {
              if (!d) return;
              const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              setTexto(isoParaBR(iso));
              onChange(iso);
              setAberto(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
